"use client";

import { useState } from "react";
import PreSurveyCheckInSection from "@/app/(participant-protected)/presurvey/_components/PreSurveyCheckInSection";
import BaselineCaptureCard from "@/features/participant/components/BaselineCaptureCard";
import ParticipantFlowPageShell from "@/features/participant-flow/components/ParticipantFlowPageShell";
import { useParticipantSurveyDraft } from "@/features/participant-flow/hooks/useParticipantSurveyDraft";
import { useStore } from "@/store";
import LocalMp3Player from "./LocalMp3Player";

export default function LocalMp3Experience() {
  const [isBaselineCapturing, setIsBaselineCapturing] = useState(false);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const baseline = useStore.use.baselineDraft();
  const setBaseline = useStore.use.setBaselineDraft();
  const {
    energy,
    pain,
    positivity,
    setEnergy,
    setPain,
    setPositivity,
  } = useParticipantSurveyDraft("pre");

  const capturedBaselineAdc =
    baseline?.gsrSmoothAdc ?? baseline?.gsrRawAdc ?? null;
  const isBaselineComplete = capturedBaselineAdc != null;
  const canOpenPlayer = isBaselineComplete && !isBaselineCapturing;

  if (isPlayerOpen) {
    return <LocalMp3Player />;
  }

  return (
    <ParticipantFlowPageShell
      eyebrow="Local MP3 session"
      title="Connect your IMAGS device"
      description="Complete the survey, connect the device over Bluetooth, and record a resting baseline before opening the MP3 player."
      bodyClassName="grid min-h-0 content-start gap-4 pb-2 xl:grid-cols-[minmax(0,0.9fr)_minmax(22rem,1.1fr)] xl:items-start xl:gap-5"
    >
      <PreSurveyCheckInSection
        pain={pain}
        energy={energy}
        positivity={positivity}
        onPainChange={setPain}
        onEnergyChange={setEnergy}
        onPositivityChange={setPositivity}
      />

      <div className="grid min-w-0 gap-3">
        <BaselineCaptureCard
          baseline={baseline}
          hasBaseline={isBaselineComplete}
          onBaselineCaptured={setBaseline}
          onCaptureStateChange={setIsBaselineCapturing}
          onClearBaseline={() => {
            setBaseline(null);
          }}
          canStartSession={canOpenPlayer}
          onStartSession={() => {
            setIsPlayerOpen(true);
          }}
          startSessionLabel="Open MP3 player"
          startSessionMessage="Your survey and baseline stay in this browser session and are not uploaded."
          startSessionDisabledReason={
            isBaselineCapturing
              ? "Wait for the baseline capture to finish."
              : "Connect the IMAGS device and record a 10-second baseline."
          }
        />

        <p className="theme-copy-muted px-1 text-xs leading-5">
          Local mode does not require an account or music-provider credentials.
          Survey and sensor values remain in this browser session.
        </p>
      </div>
    </ParticipantFlowPageShell>
  );
}
